import { IRowData } from "@multiweb/types/contracts";
import { ErrorHandler } from "UI/Errors";
import { ControlRightTypesGroup } from "UI/UI/Rights/RightGroups";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { IDataSource } from "../../../../../Global/Base/DataSources/Interfaces";
import { Event as GlobalEvent, StatefulEvent } from "../../../../../Global/Base/Events";
import { InvalidOperationException } from "../../../../../Global/Base/Exceptions";
import { CancellationTokenSource } from "../../../../../Global/Base/Promises/CancellationTokenSource";
import { Deferred, PromiseUtils } from "../../../../../Global/Base/Promises/PromiseUtils";
import { EMPTY_ITEM_RIGHTS_COLLECTION } from "../../../../../Global/Base/Rights/RightTypes";
import { StringUtils } from "../../../../../Global/Base/Utils/StringUtils";
import { OWExceptions } from "../../../../../OW.Remoting/Basic/OWExceptions";
import { AttrValueReader } from "../../../../../OW.Remoting/Remoting/AttrValueReader";
import { OWObject } from "../../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { LocalizationUtils } from "../../../../../OW.Remoting/Remoting/LocalizationUtils";
import { OWManager } from "../../../../../OW.Remoting/Remoting/OWManager";
import { IImplicitFormInfo } from "../../../../../OW.Remoting/Remoting/OWManagerInterfaces";
import { EventHelper } from "../../../../UI.BaseControls/Helpers/EventHelper";
import { ActionType } from "../../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../../UI.Controls/Actions/MenuType";
import { CalendarGrid, DayOfWeek, IntervalDirection } from "../../../../UI.Controls/CalendarGrid";
import { CalendarEvent } from "../../../../UI.Controls/CalendarGrids/CalendarEvent";
import { DayCalendarGrid } from "../../../../UI.Controls/CalendarGrids/DayCalendarGrid";
import { MonthCalendarGrid } from "../../../../UI.Controls/CalendarGrids/MonthCalendarGrid";
import { WeekCalendarGrid } from "../../../../UI.Controls/CalendarGrids/WeekCalendarGrid";
import { CalendarItem } from "../../../../UI.Controls/DataGridSubcontrols/Columns/CalendarItem";
import { ColumnFactory, IColumnDefinition } from "../../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { CalendarDataRow } from "../../../../UI.Controls/DataGridSubcontrols/Components/CalendarDataRow";
import { IDataRowElement } from "../../../../UI.Controls/DataGridSubcontrols/Components/IDataRow";
import { AggregatedObjectDataSource } from "../../../../UI.Controls/DataSource/AggregatedObjectDataSource";
import { EditableDBDataSource, SaveResultData } from "../../../../UI.Controls/DataSource/EditableDBDataSource";
import { ICalendarTooltipCreator } from "../../../../UI.Controls/ICalendarTooltip";
import { IDataGrid } from "../../../../UI.Controls/IDataGrid";
import { IDataGridSelectionChangedEvent } from "../../../../UI.Controls/IEvent";
import { createElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { convertFormOpenTypeToPurposeType } from "../../../Utilities/SharedUIMethods";
import { Control } from "../../Controls/Control";
import { FormControlWithLoggerParams } from "../../Controls/ControlInterfaces";
import { ControlUtils } from "../../Controls/ControlUtils";
import { CalendarDateTextBuilder } from "../../DataBuilders/CalendarDateTextBuilder";
import { LocalizedStringBuilder } from "../../DataBuilders/LocalizedStringBuilder";
import { FormManager } from "../../FormManager/IFormManager";
import { MessageButtonType, MessageWindow } from "../../MessageWindow";
import { UIManager } from "../../UIManager";
import { ShowInsertAction } from "../Actions/ShowInsertAction";
import { StandardAction } from "../Actions/StandardAction";
import { StandardRecordAction } from "../Actions/StandardRecordAction";
import { ModalWindowType } from "../ModalWindow";
import { ObjectCheckingResult, SharedMethods } from "../SharedMethods";
import { BaseFrame } from "./BaseFrame";
import { CalendarFrameRightsEvaluationOverrides } from "./CalendarFrameRightsEvaluationOverrides";
import { IMasterableFrameWithDataGrid } from "./FrameInterfaces";
import { FrameSharedMethods } from "./FrameSharedMethods";
import { GenericListFrame } from "./GenericListFrame";
import { ICalendarFrame } from "./IDetailMasterableFrame";
import { ListFrame } from "./ListFrame";

export class CalendarFrame extends BaseFrame implements ICalendarFrame {
    //#region Fields
    public readonly updateDate: GlobalEvent<CalendarFrame, Date> = new GlobalEvent<CalendarFrame, Date>();
    public readonly updateRange: GlobalEvent<CalendarFrame, CalendarType> = new GlobalEvent<CalendarFrame, CalendarType>();
    public override readonly dataGridInitialized: StatefulEvent<IMasterableFrameWithDataGrid, IDataGrid<IDataRowElement>> = new StatefulEvent<IMasterableFrameWithDataGrid, IDataGrid<IDataRowElement>>();

    private _dataSource: AggregatedObjectDataSource<EditableDBDataSource>;

    /**
     * Měsíční grid. NEpřistupovat k němu přímo, ale přes property currentDataGrid, pro zachování lazy inicializace.
     */
    private __monthDataGrid: CalendarGrid | undefined;
    /**
     * Denní grid. NEpřistupovat k němu přímo, ale přes property currentDataGrid, pro zachování lazy inicializace.
     */
    private __dayDataGrid: CalendarGrid | undefined;
    /**
     * Týdenní grid. NEpřistupovat k němu přímo, ale přes property currentDataGrid, pro zachování lazy inicializace.
     */
    private __weekDataGrid: CalendarGrid | undefined;

    private static _isStaticInitialized = false;
    private _calendarType: CalendarType = CalendarType.month;
    private _curDate: Date;
    private _firstDayOfWeek: DayOfWeek = DayOfWeek.monday;

    private _columnsCreated: boolean = false;
    private _columns = new Array<CalendarItem>();
    protected _openDetailInNewWindow: boolean = false;
    protected _detailIsOpened: boolean = false;
    // Pomocne promenne pro nenacitani hodnot a prav skrytych elementu
    private _cvlMasterChangeRequest: boolean = false;

    private _canValueLoadChangedListener: (s: Control, val: boolean) => {
        // do nothing.
    };
    /**
     * CancellationTokenSource posledního úplného čtení dat framu
     */
    private _loadFullFrameDataCTS: CancellationTokenSource;

    //formUI actions
    private _switchDay: StandardAction;
    private _switchMonth: StandardAction;
    private _switchWeek: StandardAction;
    //private _switchYear: StandardAction;
    private _actualObjectComplexKey: string;
    // end - formUI actions

    private _loaderCounter = 0;

    private _dateToFld?: string;
    private _dateFromFld?: string;

    protected override __rightsEvaluationOverrides: CalendarFrameRightsEvaluationOverrides;
    //#endregion Fields

    //#region Properties
    protected _formObjectName: string;
    public get formObjectName(): string {
        return this._formObjectName;
    }
    public override get visible(): boolean {
        return super.visible;
    }

    public override set visible(value: boolean) {
        const oldVisible = super.visible;
        super.visible = value;
        if (value && value !== oldVisible && this._cvlMasterChangeRequest) {
            this._onMasterObjectChanged();
        }

        this.visibilityChanged.trigger(this, value);
    }

    public override get objectName(): string {
        if (this.currentDataGrid?.mouseOverItem)
            return this.currentDataGrid.mouseOverItem.objectName;
        return super.objectName;
    }
    /** Na CF nemá setter smysl, ale musí tu být kvůli kompatibilitě s předkem */
    public override set objectName(value: string) {
        super.objectName = value; //potřeba předat na předka, aby se spustil side effect pro vyhodnocení supportsEditContext
    }

    /** CF může pracovat nad více třídami a nemůže jednoznačně rozhodnout */
    public override get supportsEditContext(): Promise<boolean | null> {
        return null;
    }

    public get actualObjectComplexKey(): string {
        return this._actualObjectComplexKey;
    }

    public get currentDate(): Date {
        return this._curDate;
    }

    public set currentDate(value: Date) {
        if (this._curDate == value)
            return;
        this._curDate = value;

        this.currentDataGrid.currentDate = value;

        this.updateDate.trigger(this, value);

        this._reloadFullFrameData();
    }

    public get calendarType(): CalendarType {
        return this._calendarType;
    }

    public get currentDataGrid(): CalendarGrid {
        return this._getDataGrid(this._calendarType);
    }

    private _getDataGrid(type: CalendarType): CalendarGrid {
        switch (type) {
            case CalendarType.day:
                return this.__dayDataGrid;
            case CalendarType.week:
                return this.__weekDataGrid;
            case CalendarType.month:
            default:
                return this.__monthDataGrid;
        }
    }

    protected override _setActualObject(object: OWObject, isFromMasterObjChanged: boolean = false, _releasePrevInstance: boolean = true, objectComplexDataKey?: string, uniqueId?: string): void {
        //TODO [PB] [WI 16749] Prozkoumat jestli nevzniká leak v InstanceManageru, protože se neuvolňují staré instance
        if (this._actualObject === object) {
            this._actualObjectComplexKey = objectComplexDataKey;
            this.contextActionsReady.trigger(this, this.actualObject.id); //kvůli tooltipu. který mohl zůstat viset při přepnutí pohledu nebo vytvoření nového objektu. V takovém případě je AO nasetován, ale jemu odpovídají dataRow (události kalendáře) jsou IsSelected==false a nevolalo by se actualObjectRightsEvaluated.
            return;
        }

        const previousActualObject = this.actualObject;

        super._setActualObject(object);

        if (object != null) {
            this._actualObjectComplexKey = objectComplexDataKey;
            if (uniqueId)
                this.currentDataGrid.uniqueDSId = uniqueId;
        } else {
            this._actualObjectComplexKey = undefined;
        }

        this.selectionChanged.trigger(this, { sender: this, objectName: object ? object.objectName : "", actualObject: object });
        this.triggerActualObjectChange(this.actualObject, previousActualObject);

        if (this.actualObject == null)
            this.currentDataGrid.actualizeDisplayedInformation(null);

        // Pokud jde o set nullu z handleru onMasterObjectChanged (tzn. jedná se o podřízený frame), jde o vymazání hodnoty před jejím (možným) nastavením na (jinou) hodnotu,
        // proto nebudu označovat načítání jako dokončené. Tak bude označeno po nastavení nové hodnoty nebo v místech, kde už je jasné,
        // že k novému nastavení nedojde (např. this._updateConditionAndRead nebo DataGrid._showData; viz reference).
        if (object || !isFromMasterObjChanged) // ala !(actObj == null && isFromMasterObjChanged)
            this._resolveActualObjectSetDeferred(true);
    }

    protected override _createOrGetRightsOverrides(): CalendarFrameRightsEvaluationOverrides {
        if (!this.__rightsEvaluationOverrides)
            this.__rightsEvaluationOverrides = new CalendarFrameRightsEvaluationOverrides(this);

        return this.__rightsEvaluationOverrides;
    }
    //#endregion Properties

    constructor(params: FormControlWithLoggerParams) {
        super(params);

        /**
         * V konstruktoru framu může být jen jednoduchá inicializace hodnot (z definice a pod.)
         * Logika a asynchronní procesy musí být až v inicializační metodě (initialize()),
         * která je vyvolávaná v potřebném pořadí a dá se na její doběhnutí čekat
         */

        CalendarFrame._staticConstructor();

        this._curDate = this._getDate(0, CalendarType.month);

        const startView = this.element.getAttribute("data-startview");
        if (!StringUtils.isNullOrWhiteSpace(startView)) {
            switch (startView.toLowerCase()) {
                case "cvmonth":
                    this._calendarType = CalendarType.month;
                    break;
                case "cvday":
                    this._calendarType = CalendarType.day;
                    break;
                case "cvweek":
                    this._calendarType = CalendarType.week;
                    break;
                //case "cvyear": this._calendarType = CalendarType.year; break;
                default:
                    this._calendarType = CalendarType.month;
                    break;
            }
        }

        this.condition = this.element.getAttribute("data-condition");
        const condParamsString = this.element.getAttribute("data-conditionparamsstring") || this.element.getAttribute("data-conditionparams");
        if (condParamsString)
            this.conditionParamsAttrs = condParamsString.split(";");

        this.element.classList.add("dataGrid", "calendar-frame");
    }

    /**
     * Znicializuje celý frame. Musí být zavoláno před jeho prvním použitím. Je možné spouštět asynchronní procesy.
     */
    protected override async _initializeVisual(): Promise<void> {
        if (this._initializationStarted) // inicializace může proběhnout jen jednou
            return;

        this._initializationStarted = true;

        /**
         * V této metodě by neměl být žádný výkonný kód.
         * Mělo by zde být pouze volání metod inicializujících jednotlivé oblasti a takovém pořadí, jaké je obecně potřeba
         */

        try {
            UIManager.instance.showLoader();

            // obecná inicializace frame
            await this._initializeFrame();
            // navázání listenerů
            this._initializeListeners();
            // vytvoření menu
            await this._initializeFormUI();
            // navázání listenerů
            await this._initializeDataSource();
            // vytvoření a nastavení DataGrid
            await this._initializeDataGrid();
            // zpracování a zaregistrování akcí do menu
            await this._initializeActions();
            // inicializace práv
            await this._initializeRights();

            this.rightsManager.rightsEvaluated.add(this._listenerActualObjectRightsEvaluated.bind(this));

            this._isInitialized = true;
            this.initialized.trigger(this, undefined);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    //#region Methods
    public async setCalendarType(type: CalendarType): Promise<void> {
        if (this._calendarType == type)
            return;
        if (this.currentDataGrid)
            this.currentDataGrid.visible = false;
        const prevType = this._calendarType;
        this._calendarType = type;
        await this._switchCalendar(prevType);
        this.updateRange.trigger(this, type);
    }

    //public _setCurrentDataGrid(): void {
    //    this._logger.error("Unable to set current datagrid this way. Use setCalendarType instead.");
    //}

    private _listenerActualObjectRightsEvaluated(): void {
        if (this._openInEditPromise) {
            this._openInEditPromise.resolve();
            this._openInEditPromise = undefined;
        }
    }

    protected override _initializeListeners(): void {
        super._initializeListeners();

        if (!this.masterFrame)
            return;

        if (this.masterFrame instanceof GenericListFrame) {
            this.masterFrame.setObjectInitiated.add(() => {
                // Při začátku načítání objektu na nadřízeném framu, nastavím nový defferred, protože se zde bude taky manipulovat s AO
                this._startActualObjectSetDeferred();

                this.setObjectInitiated.trigger(this, undefined);
            });
        }

        // data se načítají až po vytvoření datagridu
        this.dataGridInitialized.add(async () => {
            // pokud už má master frame načtený objekt, tak se o tom vyvolá oznámení
            if (this.masterFrame.actualObject != null) {
                this._cvlMasterChangeRequest = true;
                this._onMasterObjectChanged();
            }

            this.masterFrame.actualObjectChanged.add(async () => {
                this._cvlMasterChangeRequest = true;
                if (this.masterFrame) {
                    await this.masterFrame.rightsEvaluationTask;
                }
            });
        });
    }

    protected _initializeDataSource(): any {
        this._dataSource = new AggregatedObjectDataSource<EditableDBDataSource>();

        this._dataSource.dataLoadedAll.add(() => {
            this._loadFullFrameDataCTS = undefined;
        });

        const stopLoadingHandler = () => {
            if (this.menu)
                this._stopLoader();
        };

        this._dataSource.dataLoaded.add(stopLoadingHandler);
        this._dataSource.loadingCancelled.add(stopLoadingHandler);

        this._dataSource.error.add((_dataSource, error) => {
            stopLoadingHandler();
            try {
                const errData = OWExceptions.parseJSON(error);
                ErrorHandler.instance.handleError(errData);
            } catch (e) {
                ErrorHandler.instance.handleError(error);
            }
        },
        );
    }

    protected async _initializeDataGrid(): Promise<void> {
        await this._initializeSpecificDataGrid(this.calendarType);

        if (this.masterFrame == null)
            await this._reloadFullFrameData();
        else
            await this._updateCondition(this._dataSource);

        if (this.masterFrame) {
            this.masterFrame.actualObjectChanged.add(() => {
                this._onMasterObjectChanged();
            });
        }

        this.dataGridInitialized.trigger(this, await this.currentDataGrid);
    }

    private async _initializeSpecificDataGrid(calendarGridType: CalendarType): Promise<CalendarGrid | undefined> {
        const preInitDataGrid = async (dataGrid: CalendarGrid) => {
            //tovární metoda pro tooltip
            if (this.menu instanceof ICalendarTooltipCreator) {
                dataGrid.registerTooltipCreator(this.menu);
            }

            if (!this._columnsCreated)
                await this._createColumns(FrameSharedMethods.getColumnsDefinition(this.element, "[data-tscitem='CalendarItem']"));
        };

        switch (calendarGridType) {
            case CalendarType.week:
                if (!this.__weekDataGrid) {
                    this.__weekDataGrid = new WeekCalendarGrid();
                    await preInitDataGrid(this.__weekDataGrid);
                    await this._initGrid(this.__weekDataGrid, true);
                }
                return this.__weekDataGrid;
            case CalendarType.day:
                if (!this.__dayDataGrid) {
                    this.__dayDataGrid = new DayCalendarGrid();
                    await preInitDataGrid(this.__dayDataGrid);
                    await this._initGrid(this.__dayDataGrid, true);
                }
                return this.__dayDataGrid;
            //case CalendarType.year:
            case CalendarType.month:
                if (!this.__monthDataGrid) {
                    this.__monthDataGrid = new MonthCalendarGrid();
                    await preInitDataGrid(this.__monthDataGrid);
                    await this._initGrid(this.__monthDataGrid, true);

                    this.__monthDataGrid.firstDayOfWeek = this._firstDayOfWeek;
                    this.__monthDataGrid.intervalChanged.add((_sender, direction) => {
                        switch (direction) {
                            case IntervalDirection.next:
                                this.nextInterval();
                                break;
                            case IntervalDirection.prev:
                                this.prevInterval();
                                break;
                        }
                    });
                    this.__monthDataGrid.calendarChangeRequest.add(async (_sender, params) => {
                        await this.setCalendarType(params.type);
                        this.currentDate = params.date;
                    });
                }
                return this.__monthDataGrid;
        }

        return undefined;
    }

    private _openInEditPromise: Deferred<void>;

    private async _initGrid(dg: CalendarGrid, isActive: boolean = false): Promise<void> {
        dg.dataSource = this._dataSource;
        dg.columns = this._columns;
        dg.currentDate = this._curDate;
        Localization.addLocDescData(dg, this.locKey, "title");
        if (!isActive)
            dg.visible = false;

        dg.addEventListener("SelectionChanged", (e: IDataGridSelectionChangedEvent) => {
            this.onSelectionChanged(e);
        });

        dg.addEventListener("calendarEventItemDblClick", async (e) => {
            const ce = (e as any).detail as CalendarEvent;
            const row = dg.dataRows.find(r => r.dataKey == ce.dataKey);

            if (!ce || !row) {
                throw new Error("Cannot open calendar item in edit via double click.");
            }

            //aktuální objekt už je vybrán
            if (ce && this._actualObjectComplexKey && ce.dataKey == this._actualObjectComplexKey) {
                UIManager.instance.showLoader();
                try {
                    await this._checkEditRecord(ce);
                } finally {
                    UIManager.instance.hideLoader();
                }
            } else { //provede změnu AO
                UIManager.instance.showLoader();
                //nachystám si promise, který se spustí po změně AO a vyhodnocení jeho práv
                this._openInEditPromise = PromiseUtils.defer<void>();
                this._openInEditPromise.promise.then(async () => {
                    await this._checkEditRecord(ce);
                    UIManager.instance.hideLoader();
                }).catch(() => UIManager.instance.hideLoader());

                row.selectWithCheckChanges();
            }
        });

        //let vTime: number = undefined;
        this.canValueLoadChanged.add(this._canValueLoadChangedListener = async (_s: Control, val: boolean) => {
            if (this._cvlMasterChangeRequest)
                await this._onMasterObjectChanged();
            await this._newEvaluateRightsGroup(ControlRightTypesGroup.IED | ControlRightTypesGroup.initialized); // TODO-rites: ~nechat tady? asi jo; c2: spouštět až po inicializaci všech framů, jinak mohlo dojít k situaci, že se na LF iniciuje vyhodnocení práv a stromem projde na podřízený detail, který ještě není inicializován a chybí mu třeba rightsManager
            await this.checkIfIsCompact();

            if (!this.hideToolBar) //pokud není toolbar napevno skrytý
                this.menu.setVisibility(val && this.visible);
        });

        this.container.appendChild(dg);
    }

    private async _checkEditRecord(ce: CalendarEvent): Promise<void> {
        if (ce.showFormID) {
            if (!this.hideButtons.edit && this._actualObjectStandardRights.canEdit)
                await this.editRecord(ce.dataKey);
            else
                await this.openDetail(undefined, ce.dataKey);
        }
    }

    private async _switchCalendar(prevType: CalendarType): Promise<void> {
        if (!this.currentDataGrid) {
            await this._initializeSpecificDataGrid(this._calendarType);
        } else {
            this.currentDataGrid.visible = true;
        }
        if (this._calendarType == CalendarType.week) {
            if (prevType == CalendarType.day) {
                this._curDate = this._getDate(0, this._calendarType, this._curDate);
            } else if (prevType == CalendarType.month) {
                const prevDataGrid = this._getDataGrid(prevType);
                if (prevDataGrid.selectedCell) {
                    this._curDate = this._getDate(0, this._calendarType, prevDataGrid.selectedCell.idOfCell);
                } else if (new Date().getMonth() == this._curDate.getMonth() && new Date().getFullYear() == this._curDate.getFullYear()) {
                    this._curDate = this._getDate(0, this._calendarType, new Date());
                }
            } else if (this._curDate.getDay() != this._firstDayOfWeek) {
                this._curDate = this._getDate(1, this._calendarType, this._curDate); //musim nastavit interval tak aby zacinal od prvniho dne v tydnu
            }
        }
        if (this._calendarType == CalendarType.day) {
            const prevDataGrid = this._getDataGrid(prevType);
            if (prevDataGrid.selectedCell) {
                this._curDate = this._getDate(0, this._calendarType, prevDataGrid.selectedCell.idOfCell);
            }
        }

        this.currentDataGrid.currentDate = this._curDate;
        this._updateSelectedInterval();
        await this._reloadFullFrameData();
    }

    private static _staticConstructor(): void {
        if (this._isStaticInitialized)
            return;
        this._isStaticInitialized = true;

        ColumnFactory.add("CalendarItem", CalendarItem);
    }

    protected override async _initializeFormUI(): Promise<void> {
        this.menu = this.ownerForm.formUI.createFrameMenu(this);

        if (!this.menu)
            throw new InvalidOperationException("Unable to create frame menu");

        // když ještě není dataGrid vytvořený, tak se registrace naplánuje až po jeho vytvoření
        this.dataGridInitialized.add(() => {
            if (this.__dayDataGrid)
                this.__dayDataGrid.registerRowControlsFactory((dataKeyFetcher: () => string) => this.menu.renderRecordControls(dataKeyFetcher));
        });

        await this.actualizeRights(); // není objekt, na kterém by se vyhodnocovalo -> pouze vytvoří instanci s daty s defaultními právy

        // toolbar musí být vidět, pokud není nastaveno, aby byl skrytý a zároveň je vidět seznam nebo seznam má detail, který je vidět
        this.menu.setVisibility(!this.hideToolBar && (this.canValueLoad || (this.detail != null && this.detail.canValueLoad)));
    }

    protected override async _initializeActions(): Promise<void> {
        await super._initializeActions();

        if (!this.hideToolBar) {
            this._addStandartActions(this.isMainFrame);

            this.addCustomActions();
        }

        await this._newEvaluateRightsGroup(ControlRightTypesGroup.attributeChange);

        this.menu.setProperty("DateText", new CalendarDateTextBuilder(this));
        this._updateSelectedInterval();
    }

    private _updateSelectedInterval(): void {
        this._switchDay.toolbarButton.element.classList.remove("isActive");
        this._switchWeek.toolbarButton.element.classList.remove("isActive");
        this._switchMonth.toolbarButton.element.classList.remove("isActive");
        //this._switchYear.toolbarButton.element.classList.remove("isActive");

        switch (this.calendarType) {
            case CalendarType.day:
                this._switchDay.toolbarButton.element.classList.add("isActive");
                break;
            case CalendarType.week:
                this._switchWeek.toolbarButton.element.classList.add("isActive");
                break;
            case CalendarType.month:
                this._switchMonth.toolbarButton.element.classList.add("isActive");
                break;
            //case CalendarType.year:
            //    this._switchYear.toolbarButton.element.classList.add("isActive");
            //    break;
        }
    }

    protected override async _createColumns(columnsDefinition: IColumnDefinition[]): Promise<any> {
        this._columns = new Array<CalendarItem>();

        let i = 0;
        for (const definition of columnsDefinition) {
            const col = ColumnFactory.initColumn(definition, "CalendarFrame") as CalendarItem;

            if (!col.objectName)
                col.objectName = this.objectName;

            if (!col.objectName) { // přeskočení CalendarItemu - špatně nadefinovaný - nedá se zjistit třída, ke které patří
                this._logger.warn("ObjectName unavailable for CalendarItem - skipping entire CalendarItem! CalendarItem: ", definition);
                continue;
            }

            col.classOrder = i++;

            this._columns.push(col);
            //this._dataGrid.appendChild(col);

            // Povinné parametry, bez nich aplikace spadne
            const captionField = col.captionFld;
            const dateFromField = col.dateFromFld;
            const dateToField = col.dateToFld;

            if (!captionField || !dateFromField || !dateToField) {
                this._logger.warn(`Missing some of MANDATORY attributes - CaptionField: ${captionField}` +
                                `dateFromField ${dateFromField} or dateToField ${dateToField}`);
                continue;
            }

            let attributes = [captionField, dateFromField, dateToField];

            // Nepovinný parametr, když není zadán tak se nepřipojuje, aby se nepřidalo null, což by jinak spadlo.
            if (col.textFields) {
                attributes = attributes.concat(col.textFields);
            }

            const ds = new EditableDBDataSource({
                className: col.objectName,
                attributes,
                condition: col.condition,
                readVersioned: this.readVersioned,
                frameContext: this.frameContext,
                maxRecordCount: undefined,
            });

            const convertMethodFnc = (data: IRowData) => {
                data["caption"] = data[captionField];
                data["datefromfld"] = data[dateFromField];
                data["datetofld"] = data[dateToField];

                return data;
            };

            this._dataSource.registerDataSource(ds, col.objectName, col.itemId, convertMethodFnc);
        }

        if (i === 0)
            this._logger.error("No valid CalendarItem found - no data will be shown!");

        //this._dataGrid.columns = columns;
        this._dataSource.setAttrs(["caption", "datefromfld", "datetofld"]);
    }

    public createStandardRecordAction(type: ActionType, prms: (e: Event, dataKey: string) => Promise<void>, createCondition: boolean = true, defaultEnabled: boolean = true, defaultVisible: boolean = true): StandardRecordAction {
        if (createCondition) {
            const action = FrameSharedMethods.createStandardRecordAction(type, prms, this);
            action.enabled = defaultEnabled;
            action.visible = defaultVisible;
            this.menu.addAction(action);
            return action;
        } else
            return undefined;
    }

    public createStandardAction(type: ActionType, prms: () => Promise<void>, createCondition: boolean = true, defaultEnabled: boolean = true, defaultVisible: boolean = true): StandardAction {
        if (createCondition) {
            const action = FrameSharedMethods.createStandardAction(type, prms, this);
            action.enabled = defaultEnabled;
            action.visible = defaultVisible;
            this.menu.addAction(action);
            return action;
        } else
            return undefined;
    }

    private _createInsertAction(): void {
        if (this.hideToolBar || this.hideButtons.insert || this._columns.length === 0)
            return;

        const insertActions = new Array<ShowInsertAction>();
        for (const col of this._columns) {
            if (!col.canInsert || !col.showFormID)
                continue;

            const className = col.objectName || this.objectName;
            if (!className) {
                this._logger.error(`Neither CalendarFrame '${this.frameName}' nor its CalendarItem has ObjectName. Skipped.`);
                continue;
            }

            const def = createElement({ attrs: { "data-lockey": col.locKey, "data-formname": col.showFormID, "data-objectName": className, "data-color": col.color, "data-dateFromFld": col.dateFromFld, "data-dateToFld": col.dateToFld, "data-locparams": LocalizationUtils.encodeLocalizationParams(col.locParams) } });

            if (col.showFormID && col.showFormID.toUpperCase().startsWith("IMPLICIT") && !this.masterFrame) {
                this._logger.warn("Cannot create insert action. Cannot use IMPLICIT form name without master frame.");
                continue;
            }

            const action = new ShowInsertAction({ element: def, originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION });
            action.owner = this;
            action.ownerForm = this.ownerForm;
            action.enabled = false;
            action.hideButtons = { copy: true, prev: true, next: true };
            insertActions.push(action);
            action.executionStarted.add((_action, prm) => {
                this._dateFromFld = prm.dateFromFld;
                this._dateToFld = prm.dateToFld;
            });
            this.menu.addAction(action);
        }

        this._evaluateAndUpdateRightsForInsertActions(insertActions);
    }

    private async _evaluateAndUpdateRightsForInsertActions(insertActions: ShowInsertAction[]): Promise<void> {
        // práva vkládání na třídách stačí načíst jednou, ty se nevztahují na žádná data, takže se časem nezmění
        const classes = insertActions.map(a => a.objectName).filter((val, index, array) => array.indexOf(val) === index);
        const classInsertRights = new Map<string, boolean>();
        const loadingTask = Promise.all(classes.map(clazz => OWManager.getInsertRight(clazz, this._getRequestContext()).then(right => classInsertRights.set(clazz, right))));

        const updateRightsFunc = async () => {
            if (!this._actualObjectStandardRights.canNew) { // když je zakázáno zakládání na úrovni formuláře, tak se zakáží všechny akce
                insertActions.forEach(a => a.enabled = false);
                return;
            }

            await loadingTask;

            // jinak se zakáží jen ty, na jejichž třídu nemáme právo zakládání
            insertActions.forEach(a => a.enabled = classInsertRights.get(a.objectName));
        };

        await updateRightsFunc();

        if (this.masterFrame) // pro podřízené framy má význam přenastavovat práva, protože se jejich výsledek může měnit podle nadřízeného záznamu (InsertRightWithValidation)
            this.rightsManager.rightsEvaluated.add(updateRightsFunc);
    }

    protected _addStandartActions(_isMainFrame: boolean): void {
        this._createInsertAction();
        this.createStandardAction(ActionType.standardNextInterval, async () => {
            this.nextInterval();
        });
        this.createStandardAction(ActionType.standardPreviousInterval, async () => {
            this.prevInterval();
        });
        this.createStandardAction(ActionType.standardSetToday, async () => {
            this.setToday();
        });

        this._switchDay = this.createStandardAction(ActionType.standardSwitchIntervalDay, async () => {
            await this.setCalendarType(CalendarType.day);
        });
        this._switchMonth = this.createStandardAction(ActionType.standardSwitchIntervalMonth, async () => {
            await this.setCalendarType(CalendarType.month);
        });
        this._switchWeek = this.createStandardAction(ActionType.standardSwitchIntervalWeek, async () => {
            await this.setCalendarType(CalendarType.week);
        });
        //this._switchYear = this.createStandardAction(ActionType.standardSwitchIntervalYear, async () => { await this.setCalendarType(CalendarType.year); }); // bude vůbec někdy potřeba?
        this.createStandardAction(ActionType.standardRefreshList, async () => {
            await this.refreshOnClick();
        }, !this.hideButtons.refresh);

        //this.createStandardAction(ActionType.standardCopyRecord, async () => { this.createCopy(); }, !this.hideButtons.copy, false, !this.isEditing); //TODO odkomentovat při implementaci kopírování v kalendáři
        const editEnabledCond = this._actualObjectStandardRights.canEdit && this.masterFrame instanceof ListFrame && this.masterFrame.isEditable;
        this.createStandardAction(ActionType.standardEditRecord, async () => {
            await this.editRecord();
        }, !this.hideButtons.edit, editEnabledCond, !this.isEditing);
        this.createStandardAction(ActionType.standardDeleteRecord, async () => {
            await this.deleteRecord();
        }, !this.hideButtons.delete, this._actualObjectStandardRights.canDelete, !this.isEditing);
    }

    public async openDetail(e: Event, complexDataKey: string): Promise<void> {
        if (!(await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext })))
            return null;

        if (e)
            EventHelper.stopPropagation(e);
        this._openDetailModal(complexDataKey, false);
    }

    protected async _openDetailModal(complexDataKey: string, openInEdit: boolean): Promise<boolean> {
        if (this._detailIsOpened || !this.interactive) {
            this._openDetailInNewWindow = false;
            return false;
        }

        let actualObject = this.actualObject;

        UIManager.instance.showLoader();

        try {
            const calendarItemDataRow = (this.currentDataGrid.dataRows as CalendarDataRow[]).find(i => i.dataKey === complexDataKey);
            if (!calendarItemDataRow || !calendarItemDataRow.showFormID)
                throw new Error("Failed to obtain ShowFormID for selected item.");

            const [uniqueId, originalDataKey] = CalendarDataRow.getDataKeyComponents(complexDataKey);
            if (!actualObject || actualObject.id != originalDataKey) {
                this.currentDataGrid.clearSelectedRows();
                if (this.currentDataGrid.interactive) {
                    this.currentDataGrid.addSelectedRow(calendarItemDataRow);
                }
                actualObject = await this._dataSource.getObject(uniqueId, originalDataKey);
            }

            let formInfo: IImplicitFormInfo;
            try {
                formInfo = await FrameSharedMethods.getDefaultFormName(calendarItemDataRow.showFormID, this.ownerForm, actualObject, this.frameContext);
            } catch (e) {
                ErrorHandler.instance.handleError(e);
                return false;
            }

            let purposeType: MenuType | undefined = undefined;
            if (formInfo.PurposeType != null) {
                purposeType = convertFormOpenTypeToPurposeType(formInfo.PurposeType);
            }

            const afterRightOpenInEdit = !actualObject.canEdit ? false : openInEdit;

            this.hasOpenedDetailInModal = true;
            await FormManager.instance.openFormModal({
                formName: formInfo.FormName,
                parametricData: {
                    ownerForm: this.ownerForm,
                    viewMode: !this.currentDataGrid.interactive,
                    detailFormMasterFrame: this,
                    hideWindowActions: true,
                    openInEdit: afterRightOpenInEdit,
                    hideButtons: { copy: true, prev: true, next: true },
                    masterLinkToAttr: formInfo.DetailAssocName,
                    purposeType: purposeType,
                    formContext: this.ownerForm.formContext,
                    isImplicit: FrameSharedMethods.isFormNameImplicit(calendarItemDataRow.showFormID),
                },
                onClose: () => {
                    this._detailIsOpened = false;
                    this.hasOpenedDetailInModal = false;
                },
                type: ModalWindowType.parametricForm,
            }); //TODO: pridat do toho enumu i DetailFormAction?}

            this._openDetailInNewWindow = false;
            this._detailIsOpened = true;
            return true;
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     * Handler změny vybraného záznamu
     * @param event Událost o změně vybraného záznamu z DataGridu
     */
    public async onSelectionChanged(event: IDataGridSelectionChangedEvent): Promise<void> {
        if (event.detail.rowID === "-1") {
            this._setActualObject(null);
        } else {
            try {
                const complexDataKey = (event.detail as any).complexDataKey;
                const uniqueId = (event.detail as any).uniqueId;
                const originalDataKey = (event.detail as any).rowID;
                const className = this._dataSource.getClassName(uniqueId);
                if (!className) {
                    throw new Error("Failed to obtain dataSource for selected row.");
                }

                const objectInstance = await this._dataSource.getObject(uniqueId, originalDataKey);
                if (objectInstance == null) {
                    this._setActualObject(null);
                    return undefined;
                }

                this._setActualObject(objectInstance, false, true, complexDataKey, uniqueId);
            } catch (err) {
                ErrorHandler.instance.handleError(err);
            }
        }
    }

    /**
     * Metoda volana pri zmene master zaznamu
     */
    protected override async _onMasterObjectChanged(): Promise<void> {
        if (!ControlUtils.isElementVisible(this.element))
            return;

        if (this._cvlMasterChangeRequest) {
            this._cvlMasterChangeRequest = false;
        }

        if (this.actualObject)
            this._setActualObject(null, true);

        await this._reloadFullFrameData();

        if (this.masterFrame.actualObject == null || this.masterFrame.actualObject.isNew) {
            if (this.currentDataGrid)
                this.currentDataGrid.clearDataRows();
        }
    }

    /**
     * Vrátí podmínku na základě data a nastaví čas na půlnoc
     * @param value číslo udávající posun 0 = aktuální, 1 = následující, -1 = předchozí, 3 = 3. následující
     * @param type typ data ovlivňuje velikost skoku v datu danou předchozím parametrem
     * @param fromDate datum ze kterého se vycházi a tedy ke kteremu se bude skok počítat
     * @returns {Výsledné datum}
     */
    private _getDate(value: number, type: CalendarType, fromDate?: Date): Date {
        const startDate = fromDate ? new Date(fromDate) : new Date;

        if ((value == 0 && type == CalendarType.day) || value == null)
            return startDate;

        startDate.setHours(12, 0, 0, 0);

        switch (type) {
            case CalendarType.day:
                startDate.setDate(startDate.getDate() + value);
                break;
            case CalendarType.month:
                startDate.setDate(1);
                startDate.setMonth(startDate.getMonth() + value);
                break;
            case CalendarType.week:
                startDate.setDate(startDate.getDate() + value * 7);
                startDate.setDate(startDate.getDate() + this._firstDayOfWeek - startDate.getDay());
                break;
            //case CalendarType.year: startDate.setDate(1); startDate.setMonth(0); startDate.setFullYear(startDate.getFullYear() + value); break;
        }

        return startDate;
    }

    public nextInterval(): void {
        this.currentDate = this._getDate(1, this._calendarType, this._curDate);
    }

    public prevInterval(): void {
        this.currentDate = this._getDate(-1, this._calendarType, this._curDate);
    }

    public setToday(): void {
        this.currentDate = this._getDate(0, this._calendarType);
    }

    private _getDateConditionTemplate(type: CalendarType, dateFromAttrName: string, dateToAttrName: string, fromDate?: Date): string {
        const nowDate = this._getDate(0, type, fromDate);
        const nextDate = this._getDate(1, type, fromDate);

        // pro mesicni kalendar je treba nacist jeste 1 tyden pred a po, protoze je v kalendari zobrazen a vypadalo by to blbe kdyby se ty dane udalosti nenacetli i kdyz sou ty dny videt
        if (type == CalendarType.month) {
            const dayOffset = (nowDate.getDay() == 0) ? 7 : nowDate.getDay();
            nowDate.setDate(nowDate.getDate() - dayOffset + 1); // prvni den v mesicnim zobrazeni
            nextDate.setMonth(nextDate.getMonth() - 1); // musime se posunout o mesic zpet
            nextDate.setDate(42 - dayOffset + 1); // zobrazuje se 6 tydnu
        }

        const now = DataConverter.toConditionDateString(nowDate);
        const next = DataConverter.toConditionDateString(nextDate);

        return `${dateFromAttrName} <= '${next}' AND ${dateToAttrName} >= '${now}'`;
    }

    private async _reloadFullFrameData(): Promise<void> {
        this.currentDataGrid.clear();
        //const tpl = CalendarFrame._getDateConditionTemplate(this._calendarType, this._curDate);
        await this._updateCondition(this._dataSource);

        if (this.masterFrame == null || this.masterFrame.actualObject != null) { // Načti, pokud není nastaven nastaven masterFrame nebo pokud je nastaven a má vybraný objekt
            if (this._loadFullFrameDataCTS) {
                this._loadFullFrameDataCTS.cancel();
                this._loadFullFrameDataCTS = undefined;
            }
            this._loadFullFrameDataCTS = new CancellationTokenSource();
            this._startLoader(this._dataSource.length);
            return this._dataSource.loadNewData(undefined, this._loadFullFrameDataCTS.token);
        } else
            return undefined;
    }

    /**
     * Vygeneruje nove podminky a parametry pro cteni data datasourcem
     * @param ds datasource, kteremu se maji nastavit nove podminky
     */
    protected async _updateCondition(ds: IDataSource): Promise<any> {
        await super.updateCondition(ds);

        const tplFnc = (attrFrom: string, attrTo: string) => this._getDateConditionTemplate(this._calendarType, attrFrom, attrTo, this._curDate);

        for (const column of this._columns) {
            if (column.condition) {
                this._dataSource.setConditionByClassName(column.itemId, column.condition);
            }
            this._dataSource.setAdditionalConditionByClassName(column.itemId, "calendarFrameBasicRange", tplFnc(column.dateFromFld, column.dateToFld));
        }

        return Promise.resolve(undefined);
    }

    public override async newRecord(objectName?: string): Promise<boolean> {
        UIManager.instance.showLoader();

        try {
            // zkontroluje se uložení změn na masteru a jeho nadřízených framech
            if (!(await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext })))
                return false;

            if (this.masterFrame && this.masterObject == null) {
                return false;
            }

            // checkAndSaveForm() mohlo vyvolat nějaké dočítání dat na podřízených framech - je potřeba počkat, až doběhnou existující refreshe
            await this._dataSource.waitForReload();

            this._clearWorkflow();
            if (this.currentDataGrid.selectedCell && this.currentDataGrid.selectedCell.idOfCell && this._dateToFld && this._dateFromFld) {
                const timeSpan = this.currentDataGrid.selectedCellTimeSpan();
                const start = timeSpan.start.toJSON();
                const end = timeSpan.end.toJSON();

                this.afterGuiCreateFill = `${this._dateFromFld}=${start};${this._dateToFld}=${end};`;
            }
            await this.createContext();
            const newRow = await this._dataSource.newRow(objectName, await FrameSharedMethods.createAfterCreateExtendedOperations({ ownerForm: this.ownerForm, masterFrame: this.masterFrame, afterGuiCreateMethod: this.afterGuiCreateMethod, afterGuiCreateFill: this.afterGuiCreateFill, masterLinkToAttr: this.masterLinkToAttr, masterAttr: this.masterAttr, isMainFrame: this.isMainFrame }, true));
            this._setActualObject(newRow.actualObject, false, undefined);

            await this._newEvaluateRightsGroup(ControlRightTypesGroup.attributeChange);

            this._insertNewRowToDatagrid(this.actualObject);

            this.isChanged = true;

            //this.selectionChanged.trigger(this, { sender: this, objectName: this.objectName, actualObject: this.actualObject });
        } catch (err) {
            ErrorHandler.instance.handleError(err);
            return false;
        } finally {
            UIManager.instance.hideLoader();
        }

        return true;
    }

    protected _insertNewRowToDatagrid(obj: OWObject): void {
        obj.newSaved.add(() => {
            this._dataSource.loadSpecificObjectRecord(obj.objectName, obj.id, false);
        });
    }

    /**
     * Navratove hodnoty:
     * null - ulozeni probehlo v poradku
     * prazdne pole - ulozeni se nepodarilo, ale neni co zpracovat
     * naplene pole - vraci vyjimky, vznikle pri ukladani
     * @param id Pokud je vyplněn, uloží pouze jeden záznam. V opačném případě ukládá všechny rozeditované objekty.
     */
    public override async saveRecords(id?: string, checkRecommended?: boolean): Promise<SaveResultData[] | null> {
        await super.saveRecords();
        // pred ukladanim je potreba pockat na mozne probihajici updaty
        await this.waitForPendingUpdates();

        // stav před začátkem ukládání - pokud už byl seznam v readonly, tak jeho stav neměníme (třeba když je z něj otevřený modální detailový formulář, který spouští uložení)
        // jinak se přepne do readonly jen na chvíli, než doběhne ukládání záznamů
        UIManager.instance.showLoader();

        try {
            let clientValidationFail = false;
            if (!(await this.validate(checkRecommended))) {
                clientValidationFail = true;
            }

            const resultMessages = await this._dataSource.saveObjects(id ? [id + ""] : undefined); //+"" protože se objekt předával jako number a nešel porovnat pomocí indexOf

            const savingResult = await this._showValidationMessages(resultMessages);

            if ((await SharedMethods.checkObjectBeforeExecute(this.validationManager, "GUI-626119", "GUI-33412")) == ObjectCheckingResult.recommendedContinue) { //"GUI-626119"=Pokračovat,"GUI-33412"=Zrušit)
                this.validationManager.clearErrors();
                return await this.saveRecords(id, false);
            }

            if (savingResult.length > 0) {
                return savingResult;
            }

            if (clientValidationFail || this._dataSource.editedObjectsCount > 0)
                return [{ data: this.actualObject, errors: this.actualObject.getValidationErrors().map((error) => error.error) }];

            this.isEditing = false;

            if (this.detail)
                this.detail.setDetailToEditMode(false, this.interactive);

            await this._doMasterRefresh();
            this.actualObjectSaved.trigger(this, this.actualObject);

            if (!this._actualObjectComplexKey) {
                const complexDataKey = CalendarDataRow.getDataKey(this.currentDataGrid.uniqueDSId, this.actualObject.id);
                this._setActualObject(this.actualObject, false, true, complexDataKey);
            }
            await this._reloadFullFrameData();

            await this.saveContext();

            return savingResult;
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    public override async cancelChanges(): Promise<void> {
        UIManager.instance.showLoader();
        try {
            this.isEditing = false;
            this.isChanged = false;

            await this._dataSource.cancelChanges();

            // po zrušení změn se odstraňují všechny existujícíc validační chyby
            this.validationManager.clearErrors();

            if (this.detail)
                this.detail.setDetailToEditMode(false, this.interactive);

            if (this.actualObject) {
                if (this.actualObject.isNew)
                    this._setActualObject(null);
                else {
                    const actObjComplexKey = this._actualObjectComplexKey;
                    const [uniquedId, originalDataKey] = CalendarDataRow.getDataKeyComponents(actObjComplexKey);
                    this._setActualObject(await this._dataSource.getObject(uniquedId, originalDataKey), false, true, actObjComplexKey);
                }
            }

            await this.rollbackContext();
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    /**
     * Po kliknuti na Obnovit v toolbaru znovu docte data v seznamu
     */
    public override refreshOnClick(): Promise<void> {
        return this._reloadFullFrameData();
    }

    /**
     * Znovunacteni dat do framu
     * @param _manualRefresh Jestli je akce vyvolana uzivatelem (zabranuje nebo povoluju oznaceni zaznamu ve framu)
     * @param _updateOnly Zda má dojít v případě objektu k aktualizaci z paměti serveru nebo z perzistentního úložiště
     */
    public override async refreshData(_manualRefresh: boolean, _updateOnly: boolean = true): Promise<void> {
        if (await this.ownerForm.checkAndSaveForm({ cancelEditIfNotEdited: true, forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext }))
            await this._reloadFullFrameData();
    }

    public override async deleteRecord(): Promise<boolean> {
        // zkontroluje se uložení změn na masteru a jeho nadřízených framech
        if (!(await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext })))
            return false;

        const res = await MessageWindow.showYesNoDialog(new LocalizedStringBuilder("GUI-218490"), "GUI-5", "GUI-33412"); //"GUI-218490"=Skutečně chcete smazat označené záznamy?, "GUI-5"=Smazat,"GUI-33412"=Zrušit
        if (res.result === MessageButtonType.ok) {
            try {
                UIManager.instance.showLoader();

                if (this.useMasterContext)
                    await this.createContext();

                const itemsId = AttrValueReader.read(await this.actualObject.getValue("ID")) as string;
                const result = await this.actualObject.delete();
                this._removeRecordFromGrid(itemsId);
                this.clearActualObject();
                this._clearWorkflow();

                await this._doMasterRefresh();
                UIManager.instance.showResultMessages(result);
            } catch (err) {
                ErrorHandler.instance.handleError(err);
                return false;
            } finally {
                UIManager.instance.hideLoader();
            }
        }
        return true;
    }

    protected async _removeRecordFromGrid(itemId: string): Promise<void> {
        this.currentDataGrid.removeRow(itemId);
        await this._reloadFullFrameData();
    }

    /**
     * Vytvoří kopii záznamu
     * @param propagateError na true znamená, že chyby si ošetří volající metoda (např. na DetailFramu)
     */
    public override async createCopy(propagateError?: boolean): Promise<boolean> {
        //TODO: tělo zatím pouze převzato z MasterableFrame - nejspíš bude potřeba dodělat

        // Zkontroluje se uložení změn na masteru a jeho nadřízených framech
        if (!(await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext })) || !this.actualObject)
            return false;

        try {
            await this.createContext();
            const previousActualObject = this.actualObject;
            this.actualObject = await this.actualObject.createCopy();

            this._clearWorkflow();
            this._insertNewRowToDatagrid(this.actualObject);

            this.selectionChanged.trigger(this, { sender: this, objectName: this.objectName, actualObject: this.actualObject });
            this.triggerActualObjectChange(this.actualObject, previousActualObject);

            this.setToolbarToEditMode(true);

            return true;
        } catch (exception) {
            if (propagateError) {
                throw exception;
            }

            ErrorHandler.instance.handleError(exception);
        }

        return false;
    }

    //TODO
    public override clearProfileInfo(): void {
        this._logger.warn("Profiles not implemented (method clearProfileInfo)!");
    }

    public override async validate(checkRecommended?: boolean): Promise<boolean> {
        if (this.detail) {
            await this.detail.waitForPendingActions();
            await this.detail.checkEmptyComponents(checkRecommended);
        }
        // musi se zde pred vracenim vysledku znova provest waitForPendingUpdates,
        // protoze validace se sice provede a vyvola v predchozich updatech,
        // ale vola se asynchrone a je pridana do chainu tasku v pendingUpdates
        await this.waitForPendingUpdates();
        return !this.validationManager.hasErrors;
    }

    public override async validateAndShowMessage(): Promise<boolean> {
        if (await this.validate(false))
            return true;

        await this._showValidationMessages(new Array<SaveResultData>());
        return false;
    }

    /**
     * Zpracování zpráv při ukladaní záznamů
     * @param resultMessage - zpravy ze sluzby pri ukladani zaznamu
     */
    private _showValidationMessages(resultMessage: SaveResultData[]): Promise<SaveResultData[]> {
        return SharedMethods.processSaveResultsAndShowMessages(this, resultMessage);
    }

    public override async editRecord(complexDataKey?: string): Promise<boolean> {
        if (!(await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext })))
            return null;
        if (!complexDataKey && this.actualObject && this._actualObjectComplexKey)
            complexDataKey = this._actualObjectComplexKey;
        if (!complexDataKey)
            throw new Error("Unable to find record in aggregated data source. DataSource unique id must be part of record's complex dataKey.");
        return this._openDetailModal(complexDataKey, !this.hideButtons.edit);
    }

    public override prevRow(): void {
        // není využíváno
    }

    public override nextRow(): void {
        // není využíváno
    }

    private _startLoader(count: number = 1): void {
        if (this._loaderCounter === 0)
            this.menu.setProperty("loading", true);
        this._loaderCounter += count;
    }

    private _stopLoader(forceStopAll: boolean = false): void {
        if (--this._loaderCounter <= 0 || forceStopAll) {
            this.menu.setProperty("loading", false);
            if (forceStopAll || this._loaderCounter < 0)
                this._loaderCounter = 0;
        }
    }
    //#endregion Methods

    //#region IDisposable
    //destruktor
    public override dispose(): void {
        this.canValueLoadChanged.remove(this._canValueLoadChangedListener);

        super.dispose();
    }

    //#endregion IDisposable
}

export enum CalendarType {
    day,
    month,
    week,
    //year,
}
